import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBrowser, faDisplay, faVrCardboard, faMobileScreen, faTabletScreen, faLaptop, faUserSecret, faBuilding, faGlobe } from '@fortawesome/pro-solid-svg-icons'
import { AuthCloseHint, AuthHead, AuthPage, AuthScene, AuthTile } from './auth-card'
import { useReducedMotion } from '../hooks/use-reduced-motion'

// The pages that end a journey somewhere else (owner 2026-10-24: "our
// successful login screens that tell you that you should be returned to the
// app look really basic and sad and empty"): one design for all of them,
// built on the sign-in card (auth-card.js). A centred card; the scene --
// this browser, the mark, the app or device -- with its short animation;
// the title; one line on what happens next; the action where there is one;
// and "You can close this window." last.
//
// Variants (the scene's tone, glyph and two ends):
//   signedIn   an app's sign-in finished in the browser (/open/complete)
//   signedInWeb  a web sign-in held on its way back (/open/complete's notice):
//              never the app
//   used       a sign-in link the app already took (SignInLinkSpent)
//   expired    a sign-in link out of time or ended (SignInLinkSpent)
//   error      a sign-in link that didn't work (SignInLinkFailed)
//   device     another device signed in from here (the device keypad)
//   signedOut  an app's sign-out finished in the browser (/open/signed-out)
//   ended      an impersonation ended in a tab that can't close itself
//   consent    a customer's IT approved single sign-on (/open/sso-consent)
export const RETURN_VARIANTS = {
  signedIn: { tone: 'ok', glyph: 'check', from: 'browser', to: 'app' },
  signedInWeb: { tone: 'ok', glyph: 'check', from: 'browser', to: 'web' },
  used: { tone: 'ok', glyph: 'check', from: 'browser', to: 'app' },
  expired: { tone: 'warn', glyph: 'clock', from: 'browser', to: 'app', broken: true },
  error: { tone: 'bad', glyph: 'alert', from: 'browser', to: 'app', broken: true },
  device: { tone: 'ok', glyph: 'check', from: 'browser', to: 'device' },
  signedOut: { tone: 'info', glyph: 'check', from: 'app', to: 'browser' },
  ended: { tone: 'info', glyph: 'check', from: 'impersonation', to: 'browser' },
  consent: { tone: 'ok', glyph: 'check', from: 'organization', to: 'app' }
}

// The device the other end shows: the keypad's device families
// (device-identity.js: web, phone, tablet, vr, desktop).
const DEVICE_ICONS = { web: faBrowser, phone: faMobileScreen, tablet: faTabletScreen, vr: faVrCardboard, desktop: faLaptop, monitor: faDisplay }

const appTile = () =>
  <AuthTile label="EducationXR app">
    <img className="sa-tile-app" src={`${process.env.PUBLIC_URL || ''}/EDXR-Icon-flat.svg`} alt="" draggable="false" />
  </AuthTile>

const iconTile = (icon, label) =>
  <AuthTile label={label}>
    <FontAwesomeIcon icon={icon} />
  </AuthTile>

export const sceneEnd = (kind, { deviceFamily } = {}) => {
  switch (kind) {
    case 'app':
      return appTile()
    case 'browser':
      return iconTile(faBrowser, 'This browser')
    case 'web':
      return iconTile(faGlobe, 'EducationXR on the web')
    case 'device':
      return iconTile(DEVICE_ICONS[deviceFamily] || faLaptop, 'The other device')
    case 'impersonation':
      return iconTile(faUserSecret, 'Impersonation')
    case 'organization':
      return iconTile(faBuilding, 'Your organization')
    default:
      return null
  }
}

// The scene alone, for a place that already has its own frame (the device
// keypad's dialog).
export const ReturnScene = ({ variant = 'signedIn', deviceFamily }) => {
  const reducedMotion = useReducedMotion()
  const v = RETURN_VARIANTS[variant] || RETURN_VARIANTS.signedIn
  return (
    <AuthScene
      from={sceneEnd(v.from, { deviceFamily })}
      to={sceneEnd(v.to, { deviceFamily })}
      tone={v.tone}
      glyph={v.glyph}
      broken={v.broken}
      reducedMotion={reducedMotion} />
  )
}

// The whole page. `lede` is the one line on what happens next; `children`
// is anything the page must add (a notice, a support reference); `action`
// the button or link (Open the app, Sign in, Continue); `hint` the last
// line (false for none).
export const ReturnCard = ({ variant = 'signedIn', id = 'return-title', title, lede, action, hint, deviceFamily, children, testId }) =>
  <AuthPage labelledBy={id}>
    <div className={`sa-return sa-return-${variant}`} data-testid={testId || 'return-card'} data-variant={variant}>
      <ReturnScene variant={variant} deviceFamily={deviceFamily} />
      <AuthHead id={id} title={title} lede={lede} center />
      {children}
      {action && <div className="sa-actions">{action}</div>}
      {hint !== false && <AuthCloseHint>{hint || undefined}</AuthCloseHint>}
    </div>
  </AuthPage>
